/**
 * Auth + account hooks: identity, magic link, password auth, profile,
 * ARES IČO lookup, and the saved-address book.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationOptions,
  type UseQueryOptions,
} from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type {
  AuthMe,
  CustomerAddressBookEntry,
  CustomerAddressCreate,
  CustomerAddressUpdate,
  OrgSignupRequest,
  OrgSignupRequestResponse,
  OrgSignupVerifyRequest,
  OrgSignupVerifyResponse,
  PasswordResetConfirmBody,
  PasswordResetRequestBody,
  ProfileUpdateRequest,
  SetPasswordRequest,
  SignInRequest,
} from "@/api/types";

import { adminKeys, queryKeys } from "./keys";

const API = "/api/v1";

export function useAuthMe(
  options?: Omit<UseQueryOptions<AuthMe>, "queryKey" | "queryFn">,
) {
  return useQuery<AuthMe>({
    queryKey: adminKeys.me(),
    queryFn: () => apiFetch<AuthMe>(`${API}/auth/me`),
    retry: false,
    staleTime: 60 * 1000,
    ...options,
  });
}


// --- Auth mutations (magic link + logout) -----------------------------
//
// The backend magic-link flow: POST /auth/magic-link → 202, then the
// user clicks the emailed link → GET /auth/callback (sets the session
// cookie and 302-redirects to /{lang}/). The FE only needs to kick off
// step one here; the callback is handled entirely backend-side.
//
// Logout: POST /auth/logout → 204; the BE clears the cookie and we
// immediately null out the cached ``auth.me`` so the header re-renders
// as anonymous without waiting for a refetch.

interface MagicLinkRequest {
  email: string;
  /** BE negotiates lang from `?lang=` query first, then Accept-Language.
   * We pass it explicitly so the e-mail + callback URL pick up the UI
   * language the customer is actively using, not whatever the browser
   * happens to advertise in Accept-Language (chromium on CI sends
   * `en-US` regardless of what URL the user is on). */
  lang?: string;
  /** Same-origin relative path the callback should land on after the
   * session cookie is set — e.g. ``/cs/cart`` if the user was on
   * the cart page when they asked for a magic link. Whitelisted on
   * the BE (``//evil.example.com`` and friends are silently dropped)
   * so a bad value just downgrades UX to the default `/{lang}/`. */
  redirect_to?: string;
  /** "Keep me signed in" — the session minted at the callback uses the
   * longer ``REMEMBER_ME_EXPIRE_DAYS`` horizon and skips the idle
   * timeout. */
  remember_me?: boolean;
}

interface MagicLinkResponse {
  status: string;
}

export function useRequestMagicLink(
  options?: Omit<
    UseMutationOptions<MagicLinkResponse, Error, MagicLinkRequest>,
    "mutationFn"
  >,
) {
  return useMutation<MagicLinkResponse, Error, MagicLinkRequest>({
    mutationFn: ({ lang, ...body }) =>
      apiFetch<MagicLinkResponse>(`${API}/auth/magic-link`, {
        method: "POST",
        body,
        lang,
      }),
    ...options,
  });
}

interface MagicLinkMfaVerifyRequest {
  code: string;
}

/**
 * Second-factor step for a door that cannot prompt inline.
 *
 * Magic link and Google both land the browser back on `/sign-in` with a
 * short-lived challenge cookie and no session. The endpoints differ (each
 * door checks its own cookie and purpose — a shared one would let a
 * half-finished login from one door finish at the other), but the cache
 * bookkeeping afterwards is identical, so it lives here once.
 *
 * ``...options`` stays BEFORE the composed ``onSuccess``: spreading it
 * after would let a caller's own ``onSuccess`` silently replace the
 * invalidation and leave a stale ``me``.
 *
 * Each caller passes a whole ``mutationFn`` rather than just its path,
 * so both URLs stay **literal** at their call site. Threading the path
 * through as a variable reads tidier and costs the `fe-api-paths` guard
 * its teeth — it resolved the template to ``/api/v1{}`` and could no
 * longer tell whether either endpoint exists.
 */
function useMfaVerify(
  mutationFn: (payload: MagicLinkMfaVerifyRequest) => Promise<AuthMe>,
  options?: Omit<
    UseMutationOptions<AuthMe, Error, MagicLinkMfaVerifyRequest>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<AuthMe, Error, MagicLinkMfaVerifyRequest>({
    mutationFn,
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.setQueryData(adminKeys.me(), data);
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      qc.invalidateQueries({ queryKey: queryKeys.cart() });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

export function useVerifyMagicLinkMfa(
  options?: Omit<
    UseMutationOptions<AuthMe, Error, MagicLinkMfaVerifyRequest>,
    "mutationFn"
  >,
) {
  return useMfaVerify(
    (payload) =>
      apiFetch<AuthMe>(`${API}/auth/magic-link/verify-2fa`, {
        method: "POST",
        body: payload,
        skipSessionRefresh: true,
      }),
    options,
  );
}

/** KAN-174 — same step, Google door. */
export function useVerifyGoogleMfa(
  options?: Omit<
    UseMutationOptions<AuthMe, Error, MagicLinkMfaVerifyRequest>,
    "mutationFn"
  >,
) {
  return useMfaVerify(
    (payload) =>
      apiFetch<AuthMe>(`${API}/auth/google/verify-2fa`, {
        method: "POST",
        body: payload,
        skipSessionRefresh: true,
      }),
    options,
  );
}

export function useLogout(
  options?: Omit<UseMutationOptions<void, Error, void>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      await apiFetch<void>(`${API}/auth/logout`, { method: "POST" });
    },
    // See ``useAdminCreateMaterial`` — options first, then chain.
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      // Proactively wipe the cached identity so the header re-renders
      // as anonymous on the next tick. Without this, TanStack Query
      // would wait up to ``staleTime`` before re-fetching ``/auth/me``
      // and the UI would show the now-logged-out user's email.
      qc.setQueryData(adminKeys.me(), null);
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      // Sessions are identity-specific. Remove them rather than merely
      // invalidating: a subsequent sign-in otherwise renders the prior
      // account's cached list during its 30-second stale window.
      qc.removeQueries({ queryKey: adminKeys.sessions() });
      // Cart is per-session (anonymous cart token) — blow it away so
      // the next anon user doesn't inherit the previous session's
      // selections.
      qc.invalidateQueries({ queryKey: queryKeys.cart() });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}


// --- Password auth (sign-in / reset / profile) ----------------------
//
// Every mutation that establishes or alters a session prime-seeds
// ``adminKeys.me()`` with the freshly-returned ``AuthMe`` payload so
// the app shell re-renders signed-in without waiting for the next
// refetch tick. The BE sets the session cookie in the same response.
//
// Generic ``/sign-up`` was removed in favour of the role-specific
// signup pages (``/sign-up-shop`` for ProviderOrgs and ``/sign-up-team``
// for B2B CustomerOrgs); both flow through their own org-creation
// mutations rather than the bare ``POST /auth/sign-up`` endpoint.


export function useSignIn(
  options?: Omit<
    UseMutationOptions<AuthMe, Error, SignInRequest>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<AuthMe, Error, SignInRequest>({
    mutationFn: (payload) =>
      apiFetch<AuthMe>(`${API}/auth/sign-in`, {
        method: "POST",
        body: payload,
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.setQueryData(adminKeys.me(), data);
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      qc.invalidateQueries({ queryKey: queryKeys.cart() });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}


interface PasswordResetResponse {
  status: string;
}


export function usePasswordResetRequest(
  options?: Omit<
    UseMutationOptions<
      PasswordResetResponse,
      Error,
      PasswordResetRequestBody
    >,
    "mutationFn"
  >,
) {
  return useMutation<
    PasswordResetResponse,
    Error,
    PasswordResetRequestBody
  >({
    mutationFn: (payload) =>
      apiFetch<PasswordResetResponse>(
        `${API}/auth/password-reset/request`,
        { method: "POST", body: payload },
      ),
    ...options,
  });
}


/**
 * KAN-369 — set the account's FIRST password from the signed-in session.
 *
 * Not the reset flow: that one answers to the mailbox, and since KAN-345
 * the mailbox may not mint a first password for an account that already
 * signs in another way. Such an account was left with a button that
 * mailed a link the server would then refuse. This route takes the
 * session as the proof instead, so `/account` no longer has to ask the
 * inbox for something the caller has already demonstrated.
 */
export function useSetPassword(
  options?: Omit<
    UseMutationOptions<AuthMe, Error, SetPasswordRequest>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<AuthMe, Error, SetPasswordRequest>({
    mutationFn: (payload) =>
      apiFetch<AuthMe>(`${API}/auth/password`, {
        method: "POST",
        body: payload,
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      // ``has_password`` drives which half of the section renders, so
      // the cache has to learn about it or the form stays up after it
      // succeeded.
      qc.setQueryData(adminKeys.me(), data);
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}


// --- ProviderOrg self-serve signup (PR-B) ----------------------------
//
// Two-step flow that mirrors magic-link login but creates an
// Organization on verify. Request issues a magic link e-mail with
// a token; verify redeems the token, provisions the tenant schema
// and seats the caller as the org's first admin in one transaction.
// The verify response sets the session cookie BE-side, so we
// invalidate ``/auth/me`` to pick up the new identity.

export function useOrgSignupRequest(
  options?: Omit<
    UseMutationOptions<OrgSignupRequestResponse, Error, OrgSignupRequest>,
    "mutationFn"
  >,
) {
  return useMutation<OrgSignupRequestResponse, Error, OrgSignupRequest>({
    mutationFn: (payload) =>
      apiFetch<OrgSignupRequestResponse>(`${API}/orgs/signup-request`, {
        method: "POST",
        body: payload,
      }),
    ...options,
  });
}


export function useOrgSignupVerify(
  options?: Omit<
    UseMutationOptions<OrgSignupVerifyResponse, Error, OrgSignupVerifyRequest>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<OrgSignupVerifyResponse, Error, OrgSignupVerifyRequest>({
    mutationFn: (payload) =>
      apiFetch<OrgSignupVerifyResponse>(`${API}/orgs/signup-verify`, {
        method: "POST",
        body: payload,
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      // BE has set the session cookie on a 201 — refetch /auth/me so
      // the header switches to the new owner identity.
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}


// --- signup-prepare-card (signup-hardening) ----------------------
//
// Mints the Stripe Customer + SetupIntent before the FE calls
// stripe.confirmSetup. Returns the client_secret the OrgsVerify page
// hands to Stripe.js + the publishable key the page uses to initialise
// loadStripe. Idempotent on the magic-link token (server-side).

export interface SignupPrepareCardResponse {
  client_secret: string | null;
  publishable_key: string | null;
  setup_intent_id: string;
}

export function useOrgSignupPrepareCard(
  options?: Omit<
    UseMutationOptions<SignupPrepareCardResponse, Error, { token: string }>,
    "mutationFn"
  >,
) {
  return useMutation<SignupPrepareCardResponse, Error, { token: string }>({
    mutationFn: (payload) =>
      apiFetch<SignupPrepareCardResponse>(`${API}/orgs/signup-prepare-card`, {
        method: "POST",
        body: payload,
      }),
    ...options,
  });
}


export function usePasswordResetConfirm(
  options?: Omit<
    UseMutationOptions<AuthMe, Error, PasswordResetConfirmBody>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<AuthMe, Error, PasswordResetConfirmBody>({
    mutationFn: (payload) =>
      apiFetch<AuthMe>(`${API}/auth/password-reset/confirm`, {
        method: "POST",
        body: payload,
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.setQueryData(adminKeys.me(), data);
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}


/**
 * Skip the second-method offer for the next 30 days (KAN-346).
 *
 * The endpoint answers with the whole ``AuthMe`` rather than 204, so the
 * cache is written from the server's answer instead of guessing locally
 * that the offer is gone. The 30-day rule has one implementation and it
 * is not this file.
 *
 * ⚠️ ``...options`` goes BEFORE the composed ``onSuccess`` — spreading it
 * after would let a caller's own handler replace the cache write rather
 * than run alongside it (guard: ``fe-mutation-options-order``).
 */
export function useDismissAuthMethodsPrompt(
  options?: Omit<UseMutationOptions<AuthMe, Error, void>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<AuthMe, Error, void>({
    mutationFn: () =>
      apiFetch<AuthMe>(`${API}/auth/auth-methods-prompt/dismiss`, {
        method: "POST",
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.setQueryData(adminKeys.me(), data);
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}


export function useUpdateProfile(
  options?: Omit<
    UseMutationOptions<AuthMe, Error, ProfileUpdateRequest>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<AuthMe, Error, ProfileUpdateRequest>({
    mutationFn: (payload) =>
      apiFetch<AuthMe>(`${API}/auth/profile`, {
        method: "PATCH",
        body: payload,
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.setQueryData(adminKeys.me(), data);
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}


// --- ARES IČO lookup (Phase 6d) ---------------------------------------
//
// Fired on demand (button click) rather than on-change — ARES is a
// public service and we don't want to spam it as the user types. Also
// not cached: a company can update its name/address and the customer
// is better served by a fresh read than a stale one.

export interface AresLookupResult {
  ico: string;
  name: string;
  dic: string | null;
  street: string | null;
  city: string | null;
  postal_code: string | null;
  country_code: string;
  /** KAN-1107 — ARES carries a DIČ for this IČO, so a form can pre-tick
   *  „plátce DPH". A SUGGESTION: the stored flag is owned by „DPH a daně"
   *  (``PUT /admin/vat-settings``), the only writer that enforces
   *  „plátce ⇒ DIČ povinné". */
  vat_payer: boolean;
}

export function useAresLookup(
  options?: Omit<
    UseMutationOptions<AresLookupResult, Error, string>,
    "mutationFn"
  >,
) {
  return useMutation<AresLookupResult, Error, string>({
    mutationFn: (ico: string) =>
      apiFetch<AresLookupResult>(`${API}/ares/${encodeURIComponent(ico)}`),
    ...options,
  });
}


// --- Account: saved address book (post-#82) -------------------------


const ADDRESSES_KEY = ["account", "addresses"] as const;


export function useMyAddresses(
  options?: Omit<
    UseQueryOptions<CustomerAddressBookEntry[]>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<CustomerAddressBookEntry[]>({
    queryKey: ADDRESSES_KEY,
    queryFn: () =>
      apiFetch<CustomerAddressBookEntry[]>(`${API}/account/addresses`),
    staleTime: 30_000,
    retry: false,
    ...options,
  });
}


export function useCreateAddress(
  options?: Omit<
    UseMutationOptions<
      CustomerAddressBookEntry,
      Error,
      CustomerAddressCreate
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    CustomerAddressBookEntry,
    Error,
    CustomerAddressCreate
  >({
    mutationFn: (payload) =>
      apiFetch<CustomerAddressBookEntry>(`${API}/account/addresses`, {
        method: "POST",
        body: payload,
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: ADDRESSES_KEY });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}


interface UpdateAddressVars {
  addressId: number;
  patch: CustomerAddressUpdate;
}


export function useUpdateAddress(
  options?: Omit<
    UseMutationOptions<
      CustomerAddressBookEntry,
      Error,
      UpdateAddressVars
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    CustomerAddressBookEntry,
    Error,
    UpdateAddressVars
  >({
    mutationFn: ({ addressId, patch }) =>
      apiFetch<CustomerAddressBookEntry>(
        `${API}/account/addresses/${addressId}`,
        { method: "PATCH", body: patch },
      ),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: ADDRESSES_KEY });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}


export function useDeleteAddress(
  options?: Omit<UseMutationOptions<void, Error, number>, "mutationFn">,
) {
  const qc = useQueryClient();
  return useMutation<void, Error, number>({
    mutationFn: (addressId) =>
      apiFetch<void>(`${API}/account/addresses/${addressId}`, {
        method: "DELETE",
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: ADDRESSES_KEY });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}


// --- Active sessions (PR 07 — refresh tokens) -------------------------
//
// The plan documented these as ``GET /account/sessions`` etc; the BE
// shipped them under ``/auth/sessions`` (the auth router owns refresh
// rotation, so the read endpoint stayed on the same surface). The
// query hook calls the real path; the testid registry tracks the
// FE component name independently of the URL.

export interface ActiveSessionOut {
  id: number;
  issued_at: string;
  last_used_at: string;
  expires_at: string;
  ip_truncated: string | null;
  user_agent: string | null;
  /** The session the listing request itself ran under ("this
   * device"). Optional so the FE degrades gracefully against an API
   * that doesn't send it yet. */
  current?: boolean;
  /** Host this session was minted on, and the tenant that host names
   * (KAN-680). Cookies are host-only outside the platform zone, so one
   * browser holds one session per tenant it has opened — these are what
   * let the panel show them as doors of one device rather than as
   * separate devices.
   *
   * ``org_*`` is null for the platform console (no tenant) and for
   * sessions minted before the host was recorded. Optional for the same
   * reason ``current`` is: an older API simply omits them. */
  host?: string | null;
  org_slug?: string | null;
  org_name?: string | null;
}

export function useActiveSessions(
  options?: Omit<
    UseQueryOptions<ActiveSessionOut[]>,
    "queryKey" | "queryFn"
  >,
) {
  return useQuery<ActiveSessionOut[]>({
    queryKey: adminKeys.sessions(),
    queryFn: () =>
      apiFetch<ActiveSessionOut[]>(`${API}/auth/sessions`),
    retry: false,
    staleTime: 30 * 1000,
    ...options,
  });
}

interface SessionRevokeResponse {
  revoked: boolean;
}

export function useRevokeSession(
  options?: Omit<
    UseMutationOptions<SessionRevokeResponse, Error, number>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<SessionRevokeResponse, Error, number>({
    mutationFn: (sessionId) =>
      apiFetch<SessionRevokeResponse>(`${API}/auth/sessions/revoke`, {
        method: "POST",
        body: { session_id: sessionId },
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: adminKeys.sessions() });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

interface SessionRevokeAllResponse {
  revoked_count: number;
}

export function useRevokeAllSessions(
  options?: Omit<
    UseMutationOptions<SessionRevokeAllResponse, Error, void>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<SessionRevokeAllResponse, Error, void>({
    mutationFn: () =>
      apiFetch<SessionRevokeAllResponse>(
        `${API}/auth/sessions/revoke-all`,
        { method: "POST" },
      ),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.invalidateQueries({ queryKey: adminKeys.sessions() });
      // Revoke-all kills the current session too on the next refresh
      // tick — proactively wipe the cached identity so the header
      // and Account page re-render as anonymous immediately.
      qc.setQueryData(adminKeys.me(), null);
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}


// --- 2FA enrollment + verify (PR 09) ---------------------------------

interface TotpEnrollResponse {
  secret: string;
  provisioning_uri: string;
}

export function useEnroll2fa(
  options?: Omit<
    UseMutationOptions<TotpEnrollResponse, Error, { current_code?: string }>,
    "mutationFn"
  >,
) {
  return useMutation<TotpEnrollResponse, Error, { current_code?: string }>({
    mutationFn: (payload) =>
      apiFetch<TotpEnrollResponse>(`${API}/auth/2fa/enroll`, {
        method: "POST",
        body: payload,
      }),
    ...options,
  });
}

export function useVerify2fa(
  options?: Omit<
    UseMutationOptions<AuthMe, Error, { code: string }>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<AuthMe, Error, { code: string }>({
    mutationFn: (payload) =>
      apiFetch<AuthMe>(`${API}/auth/2fa/verify`, {
        method: "POST",
        body: payload,
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.setQueryData(adminKeys.me(), data);
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

/** KAN-394 — verify a bootstrap challenge without pretending it is a session. */
export function useBootstrapVerify2fa() {
  return useMutation<AuthMe, Error, { code: string }>({
    mutationFn: (payload) =>
      apiFetch<AuthMe>(`${API}/auth/2fa/verify`, {
        method: "POST",
        body: payload,
      }),
  });
}


export function useDisable2fa(
  options?: Omit<
    UseMutationOptions<AuthMe, Error, { code: string }>,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<AuthMe, Error, { code: string }>({
    mutationFn: (payload) =>
      apiFetch<AuthMe>(`${API}/auth/2fa`, {
        method: "DELETE",
        body: payload,
      }),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      qc.setQueryData(adminKeys.me(), data);
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}

// --- Email-change verify flow (PR 14) --------------------------------

interface EmailChangeRequestBody {
  new_email: string;
}

interface EmailChangeRequestResponse {
  status: string;
}

export function useRequestEmailChange(
  options?: Omit<
    UseMutationOptions<
      EmailChangeRequestResponse,
      Error,
      EmailChangeRequestBody
    >,
    "mutationFn"
  >,
) {
  return useMutation<
    EmailChangeRequestResponse,
    Error,
    EmailChangeRequestBody
  >({
    mutationFn: (payload) =>
      apiFetch<EmailChangeRequestResponse>(
        `${API}/account/email-change/request`,
        { method: "POST", body: payload },
      ),
    ...options,
  });
}

interface EmailChangeConfirmBody {
  token: string;
}

/**
 * Response of ``POST /account/email-change/confirm`` — the BE's
 * ``AccountMeResponse``, which is a strict *subset* of ``AuthMe``
 * (no ``role`` / ``has_password`` / ``ui_theme``). Deliberately typed
 * separately: writing it into the ``adminKeys.me()`` cache with
 * ``setQueryData`` — the way ``usePasswordResetConfirm`` does, because
 * *that* endpoint returns a full ``AuthMe`` — would silently drop the
 * role gate on the admin surface. Invalidate instead and let
 * ``/auth/me`` refetch the whole identity.
 */
interface EmailChangeConfirmResponse {
  id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  company: string | null;
  ico: string | null;
  dic: string | null;
  preferred_lang: string;
  preferred_currency: string;
}

/**
 * Redeem the token from the verify e-mail and flip ``users.email``.
 *
 * Unauthenticated by design — the link is opened from the NEW inbox,
 * which is routinely a different device/browser than the one that
 * requested the change. The token is the only credential.
 */
export function useConfirmEmailChange(
  options?: Omit<
    UseMutationOptions<
      EmailChangeConfirmResponse,
      Error,
      EmailChangeConfirmBody
    >,
    "mutationFn"
  >,
) {
  const qc = useQueryClient();
  return useMutation<
    EmailChangeConfirmResponse,
    Error,
    EmailChangeConfirmBody
  >({
    mutationFn: (payload) =>
      apiFetch<EmailChangeConfirmResponse>(
        `${API}/account/email-change/confirm`,
        { method: "POST", body: payload },
      ),
    ...options,
    onSuccess: async (data, variables, onMutateResult, ctx) => {
      // Best-effort: only lands when the link happens to be opened in a
      // browser that already carries the session cookie.
      qc.invalidateQueries({ queryKey: adminKeys.me() });
      await options?.onSuccess?.(data, variables, onMutateResult, ctx);
    },
  });
}


// --- Sign-up address verification (KAN-335) -----------------------
//
// Neither of these touches ``adminKeys.me()``. Confirming an address
// deliberately does not establish a session BE-side — the link travels
// through a mailbox — so seeding the me-cache here would render the
// shell signed-in on the strength of an e-mail link.

export interface VerifyEmailBody {
  token: string;
}

export interface VerifyEmailResponse {
  email: string;
}

export function useVerifyEmail(
  options?: Omit<
    UseMutationOptions<VerifyEmailResponse, Error, VerifyEmailBody>,
    "mutationFn"
  >,
) {
  return useMutation<VerifyEmailResponse, Error, VerifyEmailBody>({
    mutationFn: (payload) =>
      apiFetch<VerifyEmailResponse>(`${API}/auth/verify-email`, {
        method: "POST",
        body: payload,
      }),
    ...options,
  });
}

export interface ResendVerificationBody {
  email: string;
}

export interface ResendVerificationResponse {
  ok: boolean;
}

export function useResendVerification(
  options?: Omit<
    UseMutationOptions<
      ResendVerificationResponse,
      Error,
      ResendVerificationBody
    >,
    "mutationFn"
  >,
) {
  return useMutation<
    ResendVerificationResponse,
    Error,
    ResendVerificationBody
  >({
    mutationFn: (payload) =>
      apiFetch<ResendVerificationResponse>(
        `${API}/auth/verify-email/resend`,
        { method: "POST", body: payload },
      ),
    ...options,
  });
}


// --- Cross-host session hop --------------------------------------

export interface StartSessionTransferResponse {
  slug: string;
  /** One-time token carrying this session to `slug`'s host, or `null`
   * when the hop needs no handover (the hosts share a cookie, or the
   * deploy has no tenant apex). `null` is a normal answer — navigate
   * straight to the destination. 60-second TTL, single use, bound to
   * that one host. Redeem it ON the destination via `/auth/continue`. */
  transfer_token: string | null;
}

/**
 * POST ``/auth/session-transfer/start`` — mint a token that carries
 * the session to a tenant host the caller is entitled to reach.
 *
 * Distinct from ``useSwitchOrg``, which also mints: that one is the
 * provider-org admin *changing which tenant they administer*, so it
 * audits the switch and resolves an admin landing page. This one is
 * every other hop — open a buyer workspace, follow an accepted
 * invitation, open a changelog CTA in your own admin — including the
 * buyer case, which ``switch-org`` answers 404 because a buyer is not
 * an OrganizationMember.
 */
export function useStartSessionTransfer(
  options?: Omit<
    UseMutationOptions<StartSessionTransferResponse, Error, { slug: string }>,
    "mutationFn"
  >,
) {
  return useMutation<StartSessionTransferResponse, Error, { slug: string }>({
    mutationFn: (vars) =>
      apiFetch<StartSessionTransferResponse>(
        `${API}/auth/session-transfer/start`,
        { method: "POST", body: vars },
      ),
    ...options,
  });
}


// --- Org switcher (audit follow-up: vize §1 + §4) ----------------

interface SwitchOrgResponse {
  slug: string;
  redirect_url: string;
  /** One-time token that carries the session to `redirect_url`'s host.
   * Present exactly when the hop crosses a cookie boundary — session
   * cookies are host-only outside the platform zone. Redeem it on the
   * DESTINATION host via `/auth/continue`; anywhere else mints the
   * cookie in the wrong place. 60-second TTL, single use. */
  transfer_token: string | null;
}

/**
 * Variables for ``useSwitchOrg``. ``slug`` is the target org; the
 * optional ``hint_*`` fields tell the BE which page to preserve
 * across the switch when the target tenant has access to it. The FE
 * resolves the hints from ``adminNav.ts`` (see
 * ``resolveAdminSwitchHint`` in ``OrgSwitcher.tsx``); the BE checks
 * them against the target tenant's hidden pages + entitlement blob.
 */
export interface SwitchOrgVariables {
  slug: string;
  hint_path?: string;
  hint_page_id?: string;
}

/**
 * POST ``/auth/switch-org`` — verify the calling user is a member of
 * the target ProviderOrg and return the URL to navigate to.
 *
 * The BE does NOT mint a new JWT; active-org context lives in the
 * host/path (``<slug>.forgmatic.com`` in production, with
 * ``/o/{slug}/...`` kept as the API/path-prefix fallback).
 * The component handles the actual navigation via
 * ``window.location.href`` so the new tenant context loads from a
 * fresh state.
 */
export function useSwitchOrg(
  options?: Omit<
    UseMutationOptions<SwitchOrgResponse, Error, SwitchOrgVariables>,
    "mutationFn"
  >,
) {
  return useMutation<SwitchOrgResponse, Error, SwitchOrgVariables>({
    mutationFn: (vars) =>
      apiFetch<SwitchOrgResponse>(`${API}/auth/switch-org`, {
        method: "POST",
        body: vars,
      }),
    ...options,
  });
}
